import { Trans } from '@lingui/react';
import { Icon, Text } from '@mindbaz/ui-v2';
import { useCallback, useMemo } from 'react';

import {
  BafflingProblem,
  ContainerCTA,
  ContainerCard,
  CustomText,
  Flex,
  Goal,
  Label,
  LearnMore,
  StyledButton,
  Illustration,
} from 'pages/Features/styledComponents';
import { IFeatureCard } from 'pages/Features/types';

const FEATURES = {
  SAFESEND: 'safesend',
  DASHBOARDS: 'dashboards',
  SNAPSHOT: 'snapshot',
};

const featuresName = [
  FEATURES.SAFESEND,
  FEATURES.DASHBOARDS,
  FEATURES.SNAPSHOT,
];

const FeatureCard = (props: IFeatureCard) => {
  const {
    label,
    title,
    description,
    bafflingProblem,
    goal,
    activeFeatureCTA,
    hasUserAccessFeature,
    isNewFeature,
    illustration,
    handleFeatureMoreClick,
    activeMore,
    temporaryFreeSubscription,
  } = props;

  const cardTheme = useMemo(() => {
    return isNewFeature ? 'dark' : 'default';
  }, [isNewFeature]);

  const isColorTextSecondary = useMemo(() => {
    return cardTheme === 'dark' ? 'secondary' : 'primary';
  }, [cardTheme]);

  const handleClick = () => {
    handleFeatureMoreClick(label);
  };

  const handleAccessRedirection = useCallback(() => {
    const featureWillRedirect = featuresName.find((type: string) => {
      return type === label;
    });
    window.location.replace(
      `${import.meta.env.VITE_ADMIN_URL}${featureWillRedirect}`
    );
  }, [label]);

  const handleNoAccessRedirection = useCallback(() => {
    const featureWillRedirect = featuresName.find((type: string) => {
      return type === label;
    });
    if (featureWillRedirect === FEATURES.SAFESEND)
      window.location.replace(
        `${import.meta.env.VITE_MINDBAZ_URL}${featureWillRedirect}`
      );
    else if (featureWillRedirect === FEATURES.SNAPSHOT)
      window.location.replace(
        `${import.meta.env.VITE_ADMIN_URL}offer/${featureWillRedirect}`
      );
    else
      window.location.replace(
        `${import.meta.env.VITE_ADMIN_URL}offers/${featureWillRedirect}`
      );
  }, [label]);

  return (
    <ContainerCard className={cardTheme}>
      <Flex className="justify-between">
        <div>
          <Label className={cardTheme}>
            <Text color={isColorTextSecondary}>{label.toUpperCase()}</Text>
          </Label>
          <CustomText className="title-card">
            <Text color={isColorTextSecondary} variant="h1">
              {title}
            </Text>
          </CustomText>
          <CustomText className={`my-16 subtitle-card-${cardTheme}`}>
            <Text variant="subtitle">{description}</Text>
          </CustomText>
          <ContainerCTA>
            {(label === 'snapshot' && temporaryFreeSubscription.dailyReport) ||
            (label === 'dashboards' && temporaryFreeSubscription.dashboards) ? (
              <Text variant="h3" color="pink" weight="semibold">
                <Trans
                  id="features.freeAccess"
                  message="You currently have free access"
                />
              </Text>
            ) : hasUserAccessFeature ? (
              <StyledButton onClick={handleAccessRedirection} variant="rubixo">
                <Trans
                  id="features.manageOffer"
                  message="Manage my subscription"
                />
              </StyledButton>
            ) : (
              <StyledButton
                onClick={handleNoAccessRedirection}
                variant="rubixo"
              >
                {activeFeatureCTA}
              </StyledButton>
            )}

            <Flex
              className="justify-center align-center pointer"
              onClick={handleClick}
            >
              <LearnMore color={isColorTextSecondary}>
                <Trans id="features.learnMore" message="Learn more" />
              </LearnMore>
              <Icon color="primary">
                {activeMore === label ? 'expand_less' : 'expand_more'}
              </Icon>
            </Flex>
          </ContainerCTA>
        </div>
        <div>
          <Illustration src={illustration} alt="" />
        </div>
      </Flex>

      {activeMore === label && (
        <Flex className="mt-40 gap-24 justify-center">
          <BafflingProblem className={cardTheme}>
            <Text color={isColorTextSecondary}>
              <Trans id="features.bafflingProblem" message="🤯 The puzzle" />
            </Text>
            <Text color="lighter">{bafflingProblem}</Text>
          </BafflingProblem>
          <Goal>
            <Text color={isColorTextSecondary}>
              <Trans id="features.goal" message="🎯 Your goal" />
            </Text>
            <Text color="lighter">{goal}</Text>
          </Goal>
        </Flex>
      )}
    </ContainerCard>
  );
};

export default FeatureCard;
